import {
  MoreVertical,
  ArrowUpDown,
  CheckCircle,
  Gem,
  X,
  Archive,
  Trash2,
  Edit3,
} from "lucide-react";
import { CompanyAvatar } from "./company-avatar";
import { getReportStatusIndicator } from "./utils";
import { Button } from "../ui/button";
import { Checkbox } from "../ui/checkbox";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../ui/tooltip";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
} from "../ui/dropdown-menu";
import { Link } from "react-router";
import { useState, useMemo } from "react";
import { DealTransformed } from "@/lib/schema/deal-schema";
import { formatTimeAgo } from "@/lib/utils";
import { DeleteDialog } from "./delete-dialog";
import { DeactivateDealDialog } from "./deactivate-deal-dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import { toast } from "sonner";
import { useUser } from "@clerk/clerk-react";
import { createDeal, dealsApi } from "@/api/deals-api";
import { useWorkspaceStore } from "@/store/workspace-store";
import { getLastActivityDate } from "@/services/deals-service";
import { getErrorMessage } from "@/lib/api-error";
import { BulkStatusUpdateModal } from "./bulk-status-update-modal";
import { BulkArchiveDialog } from "./bulk-archive-dialog";
import { BulkDeleteDialog } from "./bulk-delete-dialog";
import { BulkDeactivateDialog } from "./bulk-deactivate-dialog";
import { useBulkDeactivateDeals } from "@/hooks/use-bulk-deactivate-deals";

interface MobileViewProps {
  deals: DealTransformed[];
  onStatusChange: (params: { dealId: string; status: string }) => void;
  workspaceId: string;
  onDealDeleted?: () => void;
  onDealArchived?: () => void;
}

export const MobileView = ({
  deals,
  onStatusChange,
  workspaceId,
  onDealDeleted,
  onDealArchived,
}: MobileViewProps) => {
  const [selectedDealIds, setSelectedDealIds] = useState<Set<string>>(
    new Set()
  );
  const [isBulkStatusModalOpen, setIsBulkStatusModalOpen] = useState(false);
  const [isBulkArchiveModalOpen, setIsBulkArchiveModalOpen] = useState(false);
  const [isBulkDeleteModalOpen, setIsBulkDeleteModalOpen] = useState(false);
  const [isBulkDeactivateModalOpen, setIsBulkDeactivateModalOpen] =
    useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { activeWorkspace, getStatusColor } = useWorkspaceStore();

  const [deleteDialog, setDeleteDialog] = useState<{
    isOpen: boolean;
    dealId: string;
    dealName: string;
  }>({
    isOpen: false,
    dealId: "",
    dealName: "",
  });

  const [deactivateDialog, setDeactivateDialog] = useState<{
    isOpen: boolean;
    dealId: string;
    dealName: string;
  }>({
    isOpen: false,
    dealId: "",
    dealName: "",
  });

  const [sortConfig, setSortConfig] = useState<{
    [status: string]: {
      key: "name" | "lastActivity";
      direction: "asc" | "desc";
    };
  }>({});

  const { user } = useUser();
  const { deactivate: bulkDeactivate, isDeactivating: isBulkDeactivating } =
    useBulkDeactivateDeals(workspaceId);
  const premiumSelectedIds = deals
    .filter((d) => selectedDealIds.has(d.id) && d.aiTracked)
    .map((d) => d.id);

  // Bulk operations mutations
  const bulkStatusUpdateMutation = useMemo(
    () => ({
      mutateAsync: async (newStatus: string) => {
        return dealsApi.bulkChangeStatus(
          workspaceId,
          Array.from(selectedDealIds),
          newStatus,
          user?.id ?? ""
        );
      },
      isPending: false,
    }),
    [selectedDealIds, workspaceId, user?.id]
  );

  const bulkArchiveMutation = useMemo(
    () => ({
      mutateAsync: async () => {
        return createDeal.bulkCreateFlaggedCompany(
          workspaceId,
          Array.from(selectedDealIds),
          user?.id ?? ""
        );
      },
      isPending: false,
    }),
    [selectedDealIds, workspaceId, user?.id]
  );

  const bulkDeleteMutation = useMemo(
    () => ({
      mutateAsync: async () => {
        return createDeal.bulkDeleteFlaggedCompany(
          workspaceId,
          Array.from(selectedDealIds)
        );
      },
      isPending: false,
    }),
    [selectedDealIds, workspaceId]
  );

  const handleSort = (status: string, key: "name" | "lastActivity") => {
    setSortConfig((prev) => ({
      ...prev,
      [status]: {
        key,
        direction:
          prev[status]?.key === key && prev[status]?.direction === "asc"
            ? "desc"
            : "asc",
      },
    }));
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedDealIds(new Set(deals.map((deal) => deal.id)));
    } else {
      setSelectedDealIds(new Set());
    }
  };

  const handleSelectDeal = (dealId: string, checked: boolean) => {
    const newSelection = new Set(selectedDealIds);
    if (checked) {
      newSelection.add(dealId);
    } else {
      newSelection.delete(dealId);
    }
    setSelectedDealIds(newSelection);
  };

  const clearSelection = () => {
    setSelectedDealIds(new Set());
  };

  const isAllSelected =
    selectedDealIds.size === deals.length && deals.length > 0;
  const isIndeterminate =
    selectedDealIds.size > 0 && selectedDealIds.size < deals.length;

  const handleArchive = async (dealId: string) => {
    if (!workspaceId) return;
    try {
      await createDeal.createFlaggedCompany(
        workspaceId,
        dealId,
        user?.id ?? ""
      );
      onDealArchived?.();
      toast.success("Deal archived successfully");
    } catch (error) {
      console.error("Failed to archive deal:", error);
      toast.error(getErrorMessage(error, "Failed to archive deal"));
    }
  };

  const handleBulkStatusUpdate = async (newStatus: string) => {
    try {
      setIsLoading(true);
      await bulkStatusUpdateMutation.mutateAsync(newStatus);
      toast.success(
        `Successfully updated ${selectedDealIds.size} deals to new status`
      );
      setSelectedDealIds(new Set());
      // Refresh deals data by calling the parent's refresh function
      if (onDealDeleted) {
        onDealDeleted();
      }
    } catch (error) {
      console.error("Bulk status update failed:", error);
      toast.error(getErrorMessage(error, "Failed to update deal statuses"));
    } finally {
      setIsLoading(false);
    }
  };

  const handleBulkArchive = async () => {
    try {
      setIsLoading(true);
      await bulkArchiveMutation.mutateAsync();
      toast.success(`Successfully archived ${selectedDealIds.size} deals`);
      setSelectedDealIds(new Set());
      onDealArchived?.();
    } catch (error) {
      console.error("Bulk archive failed:", error);
      toast.error(getErrorMessage(error, "Failed to archive deals"));
    } finally {
      setIsLoading(false);
    }
  };

  const handleBulkDelete = async () => {
    try {
      setIsLoading(true);
      await bulkDeleteMutation.mutateAsync();
      toast.success(`Successfully deleted ${selectedDealIds.size} deals`);
      setSelectedDealIds(new Set());
      onDealDeleted?.();
    } catch (error) {
      console.error("Bulk delete failed:", error);
      toast.error(getErrorMessage(error, "Failed to delete deals"));
    } finally {
      setIsLoading(false);
    }
  };

  const handleBulkDeactivate = async () => {
    await bulkDeactivate(premiumSelectedIds, () =>
      setSelectedDealIds(new Set())
    );
  };

  return (
    <div className="space-y-2 mobile-container">
      {/* Mobile Quick Actions Toolbar */}
      {selectedDealIds.size > 0 && (
        <div className="fixed bottom-4 left-4 right-4 z-50">
          <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg p-3 animate-in slide-in-from-bottom-4 duration-300">
            {/* Selection Info */}
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <CheckCircle className="h-4 w-4 text-green-500" />
                <span className="text-sm font-medium">
                  {selectedDealIds.size} selected
                </span>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={clearSelection}
                className="h-6 w-6 p-0"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>

            {/* Quick Actions Grid */}
            <div className="grid grid-cols-3 gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setIsBulkStatusModalOpen(true)}
                disabled={isLoading}
                className="flex flex-col items-center gap-1 h-auto py-2 text-blue-600 hover:text-blue-700"
              >
                <Edit3 className="h-4 w-4" />
                <span className="text-xs">Status</span>
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setIsBulkArchiveModalOpen(true)}
                disabled={isLoading}
                className="flex flex-col items-center gap-1 h-auto py-2 text-yellow-600 hover:text-yellow-700"
              >
                <Archive className="h-4 w-4" />
                <span className="text-xs">Archive</span>
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setIsBulkDeleteModalOpen(true)}
                disabled={isLoading}
                className="flex flex-col items-center gap-1 h-auto py-2 text-red-600 hover:text-red-700"
              >
                <Trash2 className="h-4 w-4" />
                <span className="text-xs">Delete</span>
              </Button>
              {premiumSelectedIds.length > 0 && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setIsBulkDeactivateModalOpen(true)}
                  disabled={isBulkDeactivating}
                  className="flex flex-col items-center gap-1 h-auto py-2"
                >
                  <Gem className="h-4 w-4" />
                  <span className="text-xs">Not Premium</span>
                </Button>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="divide-y divide-gray-100">
        {(() => {
          const flatSort = sortConfig["__all__"];
          const flatSorted = !flatSort
            ? deals
            : [...deals].sort((a, b) => {
                if (flatSort.key === "name") {
                  return flatSort.direction === "asc"
                    ? a.overview.name.localeCompare(b.overview.name)
                    : b.overview.name.localeCompare(a.overview.name);
                }
                const dA = getLastActivityDate(a).getTime();
                const dB = getLastActivityDate(b).getTime();
                return flatSort.direction === "asc" ? dA - dB : dB - dA;
              });
          return (
            <div className="overflow-x-auto bg-background">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-12">
                      <Checkbox
                        checked={isAllSelected}
                        onCheckedChange={handleSelectAll}
                        aria-label="Select all deals"
                        className={
                          isIndeterminate
                            ? "border-foreground bg-foreground text-background shadow-none"
                            : "border-border shadow-none data-[state=checked]:bg-foreground data-[state=checked]:border-foreground data-[state=checked]:text-background"
                        }
                      />
                    </TableHead>
                    <TableHead
                      onClick={() => handleSort("__all__", "name")}
                      className="cursor-pointer hover:bg-muted text-[11px] font-semibold uppercase tracking-widest text-muted-foreground/70"
                    >
                      Deal Name
                      <ArrowUpDown
                        className={`inline h-3.5 w-3.5 ml-1.5 ${
                          flatSort?.key === "name"
                            ? "text-foreground"
                            : "text-muted-foreground/40"
                        }`}
                      />
                    </TableHead>
                    <TableHead
                      onClick={() => handleSort("__all__", "lastActivity")}
                      className="cursor-pointer hover:bg-muted text-[11px] font-semibold uppercase tracking-widest text-muted-foreground/70"
                    >
                      Last Activity
                      <ArrowUpDown
                        className={`inline h-3.5 w-3.5 ml-1.5 ${
                          flatSort?.key === "lastActivity"
                            ? "text-foreground"
                            : "text-muted-foreground/40"
                        }`}
                      />
                    </TableHead>
                    <TableHead className="w-[40px]"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {flatSorted.map((deal) => {
                    const statusIndicator = getReportStatusIndicator(
                      deal.reportStatus?.opportunityApplication?.report_status
                    );
                    return (
                      <TableRow key={deal.id}>
                        <TableCell className="w-12">
                          <Checkbox
                            checked={selectedDealIds.has(deal.id)}
                            onCheckedChange={(checked) =>
                              handleSelectDeal(deal.id, checked as boolean)
                            }
                            aria-label={`Select ${deal.overview.name}`}
                            className="border-border shadow-none data-[state=checked]:bg-foreground data-[state=checked]:border-foreground data-[state=checked]:text-background"
                          />
                        </TableCell>
                        <TableCell className="py-2">
                          <Link
                            to={`/${workspaceId}/deals/${deal.id}`}
                            className="flex items-center gap-2 group min-w-0"
                          >
                            <CompanyAvatar
                              companyFiles={deal.company_files}
                              websiteUrl={deal.overview.website}
                              alt={deal.overview.name}
                            />
                            <span className="flex items-center gap-1 min-w-0">
                              <span className="text-sm font-medium text-foreground group-hover:underline decoration-muted-foreground/30 underline-offset-2 truncate">
                                {deal.overview.name}
                              </span>
                              {deal.aiTracked && (
                                <Gem className="h-3.5 w-3.5 shrink-0 text-primary" />
                              )}
                              {deal.prospectiveAffEmail && (
                                <CheckCircle className="h-3.5 w-3.5 shrink-0 text-green-500" />
                              )}
                              {statusIndicator && (
                                <TooltipProvider>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <span className="inline-flex shrink-0">
                                        <statusIndicator.icon
                                          className={`h-3.5 w-3.5 ${
                                            statusIndicator.type === "loading"
                                              ? "text-blue-500 animate-spin"
                                              : "text-red-500"
                                          }`}
                                        />
                                      </span>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                      {statusIndicator.label}
                                    </TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                              )}
                            </span>
                          </Link>
                        </TableCell>
                        <TableCell className="font-mono text-xs tabular-nums text-muted-foreground/70 py-2">
                          {formatTimeAgo(getLastActivityDate(deal))}
                        </TableCell>
                        <TableCell className="py-2">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-7 w-7 px-0 text-muted-foreground hover:text-foreground"
                              >
                                <MoreVertical className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuSub>
                                <DropdownMenuSubTrigger>
                                  <span className="text-sm">Change Status</span>
                                </DropdownMenuSubTrigger>
                                <DropdownMenuSubContent>
                                  <DropdownMenuRadioGroup
                                    value={deal.overview.status}
                                    onValueChange={(value) => {
                                      onStatusChange({
                                        dealId: deal.id,
                                        status: value,
                                      });
                                    }}
                                  >
                                    {(activeWorkspace?.statuses || []).map(
                                      (status) => (
                                        <DropdownMenuRadioItem
                                          key={status.title}
                                          value={status.title}
                                          style={
                                            status.colour?.startsWith("#")
                                              ? {
                                                  backgroundColor: status.colour,
                                                  color: "white",
                                                }
                                              : undefined
                                          }
                                          className={
                                            !status.colour?.startsWith("#")
                                              ? getStatusColor(status.title)
                                              : ""
                                          }
                                        >
                                          {status.title}
                                        </DropdownMenuRadioItem>
                                      )
                                    )}
                                  </DropdownMenuRadioGroup>
                                </DropdownMenuSubContent>
                              </DropdownMenuSub>
                              {deal.aiTracked && (
                                <DropdownMenuItem
                                  onClick={() =>
                                    setDeactivateDialog({
                                      isOpen: true,
                                      dealId: deal.id,
                                      dealName: deal.overview.name,
                                    })
                                  }
                                >
                                  Mark as not premium
                                </DropdownMenuItem>
                              )}
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                onClick={() => handleArchive(deal.id)}
                                className="text-yellow-600"
                              >
                                Archive
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                className="text-destructive"
                                onClick={() =>
                                  setDeleteDialog({
                                    isOpen: true,
                                    dealId: deal.id,
                                    dealName: deal.overview.name,
                                  })
                                }
                              >
                                Delete
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          );
        })()}
      </div>

      {/* Bulk Operation Modals */}
      <BulkStatusUpdateModal
        isOpen={isBulkStatusModalOpen}
        onClose={() => setIsBulkStatusModalOpen(false)}
        selectedDealIds={Array.from(selectedDealIds)}
        onStatusUpdate={handleBulkStatusUpdate}
        isLoading={isLoading}
      />

      <BulkArchiveDialog
        isOpen={isBulkArchiveModalOpen}
        onClose={() => setIsBulkArchiveModalOpen(false)}
        selectedDealIds={Array.from(selectedDealIds)}
        onArchive={handleBulkArchive}
        isLoading={isLoading}
      />

      <BulkDeleteDialog
        isOpen={isBulkDeleteModalOpen}
        onClose={() => setIsBulkDeleteModalOpen(false)}
        selectedDealIds={Array.from(selectedDealIds)}
        onDelete={handleBulkDelete}
        isLoading={isLoading}
      />

      <BulkDeactivateDialog
        isOpen={isBulkDeactivateModalOpen}
        onClose={() => setIsBulkDeactivateModalOpen(false)}
        count={premiumSelectedIds.length}
        onConfirm={handleBulkDeactivate}
        isLoading={isBulkDeactivating}
      />

      <DeleteDialog
        isOpen={deleteDialog.isOpen}
        onClose={() =>
          setDeleteDialog({ isOpen: false, dealId: "", dealName: "" })
        }
        dealId={deleteDialog.dealId}
        dealName={deleteDialog.dealName}
        workspaceId={workspaceId}
        onSuccess={() => {
          onDealDeleted?.();
        }}
      />

      <DeactivateDealDialog
        isOpen={deactivateDialog.isOpen}
        onClose={() =>
          setDeactivateDialog({ isOpen: false, dealId: "", dealName: "" })
        }
        dealId={deactivateDialog.dealId}
        dealName={deactivateDialog.dealName}
        workspaceId={workspaceId}
      />
    </div>
  );
};
